import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Modal, Button } from "react-bootstrap";
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import Breadcrumbs from "../../Breadcrumbs";
import TextEditor, { normalizeHtml } from "../common/TextEditor";
import "../common/TextEditor.css";
import {
  addTaskTracker,
  addTaskComment,
  updateTaskTracker,
  updateTaskStatus,
  updateTaskPriority,
  fetchTaskTrackerList,
  resetTaskTrackerStatus,
  resetCommentStatus,
  resetStatusUpdateStatus,
  resetPriorityUpdateStatus,
  resetEditStatus,
} from "../../features/taskTrackerSlice/taskTrackerSlice";

const STATUS_OPTIONS = [
  { value: "1", label: "Pending" },
  { value: "2", label: "Done" },
  { value: "3", label: "Hold" },
  { value: "4", label: "Ongoing" },
  { value: "5", label: "Point not clear" },
  { value: "6", label: "Need approval" },
];

const STATUS_BY_VALUE = STATUS_OPTIONS.reduce((acc, opt) => {
  acc[opt.value] = opt.label;
  acc[Number(opt.value)] = opt.label;
  return acc;
}, {});

const STATUS_BADGE_CLASSES = {
  1: "bg-warning-subtle text-warning",
  2: "bg-success-subtle text-success",
  3: "bg-secondary-subtle text-secondary",
  4: "bg-info-subtle text-info",
  5: "bg-danger-subtle text-danger",
  6: "bg-primary-subtle text-primary",
};

const PRIORITY_OPTIONS = [
  { value: "1", label: "Low" },
  { value: "2", label: "Medium" },
  { value: "3", label: "High" },
  { value: "4", label: "Urgent" },
];

const PRIORITY_BY_VALUE = PRIORITY_OPTIONS.reduce((acc, opt) => {
  acc[opt.value] = opt.label;
  acc[Number(opt.value)] = opt.label;
  return acc;
}, {});

const PRIORITY_BADGE_CLASSES = {
  1: "bg-secondary-subtle text-secondary",
  2: "bg-info-subtle text-info",
  3: "bg-warning-subtle text-warning",
  4: "bg-danger-subtle text-danger",
};

const initialFormData = {
  title: "",
  description: "",
  images: [],
  priority: PRIORITY_OPTIONS[1],
};

const uploadUrl = process.env.REACT_APP_BACKEND_UPLOADS;

const TaskTracker = () => {
  const dispatch = useDispatch();
  const {
    list,
    requestStatus,
    commentStatus,
    statusUpdateStatus,
    priorityUpdateStatus,
    editStatus,
    message,
    commentMessage,
    statusUpdateMessage,
    priorityUpdateMessage,
    editMessage,
  } = useSelector((state) => state.taskTracker);
  const [formData, setFormData] = useState(initialFormData);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [expandedTaskId, setExpandedTaskId] = useState(null);
  const [commentDrafts, setCommentDrafts] = useState({});
  const [submittingCommentTaskId, setSubmittingCommentTaskId] = useState(null);
  const [updatingStatusTaskId, setUpdatingStatusTaskId] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusModalTask, setStatusModalTask] = useState(null);
  const [modalStatus, setModalStatus] = useState(STATUS_OPTIONS[0]);
  const [showPriorityModal, setShowPriorityModal] = useState(false);
  const [priorityModalTask, setPriorityModalTask] = useState(null);
  const [modalPriority, setModalPriority] = useState(PRIORITY_OPTIONS[1]);
  const [updatingPriorityTaskId, setUpdatingPriorityTaskId] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editModalTask, setEditModalTask] = useState(null);
  const [editFormData, setEditFormData] = useState({
    title: "",
    description: "",
    existingImages: [],
    newImages: [],
  });
  const [editPreviewUrls, setEditPreviewUrls] = useState([]);

  useEffect(() => {
    dispatch(fetchTaskTrackerList());
  }, [dispatch]);

  useEffect(() => {
    if (requestStatus === "success") {
      toast.success(message || "Task saved successfully");
      dispatch(resetTaskTrackerStatus());
      setFormData(initialFormData);
      setPreviewUrls([]);
      dispatch(fetchTaskTrackerList());
    } else if (requestStatus === "error") {
      toast.error(message || "Something went wrong");
      dispatch(resetTaskTrackerStatus());
    }
  }, [requestStatus, message, dispatch]);

  useEffect(() => {
    if (commentStatus === "success") {
      toast.success(commentMessage || "Comment added successfully");
      dispatch(resetCommentStatus());
      if (submittingCommentTaskId) {
        setCommentDrafts((prev) => ({ ...prev, [submittingCommentTaskId]: "" }));
        setSubmittingCommentTaskId(null);
      }
    } else if (commentStatus === "error") {
      toast.error(commentMessage || "Failed to add comment");
      dispatch(resetCommentStatus());
      setSubmittingCommentTaskId(null);
    }
  }, [commentStatus, commentMessage, dispatch, submittingCommentTaskId]);

  useEffect(() => {
    if (statusUpdateStatus === "success") {
      toast.success(statusUpdateMessage || "Status updated successfully");
      dispatch(resetStatusUpdateStatus());
      setUpdatingStatusTaskId(null);
      setShowStatusModal(false);
      setStatusModalTask(null);
    } else if (statusUpdateStatus === "error") {
      toast.error(statusUpdateMessage || "Failed to update status");
      dispatch(resetStatusUpdateStatus());
      setUpdatingStatusTaskId(null);
      dispatch(fetchTaskTrackerList());
    }
  }, [statusUpdateStatus, statusUpdateMessage, dispatch]);

  useEffect(() => {
    if (priorityUpdateStatus === "success") {
      toast.success(priorityUpdateMessage || "Priority updated successfully");
      dispatch(resetPriorityUpdateStatus());
      setUpdatingPriorityTaskId(null);
      setShowPriorityModal(false);
      setPriorityModalTask(null);
    } else if (priorityUpdateStatus === "error") {
      toast.error(priorityUpdateMessage || "Failed to update priority");
      dispatch(resetPriorityUpdateStatus());
      setUpdatingPriorityTaskId(null);
      dispatch(fetchTaskTrackerList());
    }
  }, [priorityUpdateStatus, priorityUpdateMessage, dispatch]);

  useEffect(() => {
    if (editStatus === "success") {
      toast.success(editMessage || "Task updated successfully");
      dispatch(resetEditStatus());
      setShowEditModal(false);
      setEditModalTask(null);
      setEditFormData({
        title: "",
        description: "",
        existingImages: [],
        newImages: [],
      });
      setEditPreviewUrls([]);
    } else if (editStatus === "error") {
      toast.error(editMessage || "Failed to update task");
      dispatch(resetEditStatus());
    }
  }, [editStatus, editMessage, dispatch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files || []);
    setFormData((prev) => ({ ...prev, images: files }));
    setPreviewUrls(files.map((file) => URL.createObjectURL(file)));
  };

  const removeImage = (index) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
    setPreviewUrls((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      toast.error("Title is required");
      return;
    }

    const payload = new FormData();
    formData.images.forEach((file) => {
      payload.append("images", file);
    });

    payload.append(
      "payloadData",
      JSON.stringify({
        title: formData.title.trim(),
        description: normalizeHtml(formData.description),
        priority: parseInt(formData.priority?.value || "2", 10),
      })
    );

    dispatch(addTaskTracker(payload));
  };

  const handleReset = () => {
    setFormData(initialFormData);
    setPreviewUrls([]);
  };

  const getImageUrl = (fileName) => {
    if (!fileName) return "";
    if (uploadUrl) return `${uploadUrl}/${fileName}`;
    return fileName;
  };

  const toggleComments = (taskId) => {
    setExpandedTaskId((prev) => (prev === taskId ? null : taskId));
  };

  const handleCommentDraftChange = (taskId, value) => {
    setCommentDrafts((prev) => ({ ...prev, [taskId]: value }));
  };

  const getStatusLabel = (item) =>
    item?.show_status ||
    STATUS_BY_VALUE[item?.status] ||
    STATUS_BY_VALUE[String(item?.status)] ||
    "Pending";

  const getStatusBadgeClass = (item) => {
    const statusKey = item?.status ?? 1;
    return (
      STATUS_BADGE_CLASSES[statusKey] ||
      STATUS_BADGE_CLASSES[String(statusKey)] ||
      STATUS_BADGE_CLASSES[1]
    );
  };

  const getPriorityLabel = (item) =>
    item?.show_priority ||
    PRIORITY_BY_VALUE[item?.priority] ||
    PRIORITY_BY_VALUE[String(item?.priority)] ||
    "Medium";

  const getPriorityBadgeClass = (item) => {
    const priorityKey = item?.priority ?? 2;
    return (
      PRIORITY_BADGE_CLASSES[priorityKey] ||
      PRIORITY_BADGE_CLASSES[String(priorityKey)] ||
      PRIORITY_BADGE_CLASSES[2]
    );
  };

  const openStatusModal = (task) => {
    const current =
      STATUS_OPTIONS.find((opt) => opt.value === String(task.status)) ||
      STATUS_OPTIONS[0];
    setStatusModalTask(task);
    setModalStatus(current);
    setShowStatusModal(true);
  };

  const closeStatusModal = () => {
    setShowStatusModal(false);
    setStatusModalTask(null);
  };

  const openPriorityModal = (task) => {
    const current =
      PRIORITY_OPTIONS.find((opt) => opt.value === String(task.priority)) ||
      PRIORITY_OPTIONS[1];
    setPriorityModalTask(task);
    setModalPriority(current);
    setShowPriorityModal(true);
  };

  const closePriorityModal = () => {
    setShowPriorityModal(false);
    setPriorityModalTask(null);
  };

  const confirmPriorityUpdate = () => {
    if (!priorityModalTask || !modalPriority) return;

    if (String(priorityModalTask.priority) === String(modalPriority.value)) {
      toast.info("Priority is already set to this value");
      return;
    }

    setUpdatingPriorityTaskId(priorityModalTask.id);
    dispatch(
      updateTaskPriority({
        task_id: priorityModalTask.id,
        priority: parseInt(modalPriority.value, 10),
      })
    );
  };

  const openEditModal = (task) => {
    setEditModalTask(task);
    setEditFormData({
      title: task.title || "",
      description: task.description || "",
      existingImages: [...(task.images || [])],
      newImages: [],
    });
    setEditPreviewUrls([]);
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditModalTask(null);
    setEditFormData({
      title: "",
      description: "",
      existingImages: [],
      newImages: [],
    });
    setEditPreviewUrls([]);
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditImageChange = (e) => {
    const files = Array.from(e.target.files || []);
    setEditFormData((prev) => ({ ...prev, newImages: files }));
    setEditPreviewUrls(files.map((file) => URL.createObjectURL(file)));
  };

  const removeExistingEditImage = (index) => {
    setEditFormData((prev) => ({
      ...prev,
      existingImages: prev.existingImages.filter((_, i) => i !== index),
    }));
  };

  const removeNewEditImage = (index) => {
    setEditFormData((prev) => ({
      ...prev,
      newImages: prev.newImages.filter((_, i) => i !== index),
    }));
    setEditPreviewUrls((prev) => prev.filter((_, i) => i !== index));
  };

  const confirmEditTask = () => {
    if (!editModalTask) return;

    if (!editFormData.title.trim()) {
      toast.error("Title is required");
      return;
    }

    const payload = new FormData();
    editFormData.newImages.forEach((file) => {
      payload.append("images", file);
    });

    payload.append(
      "payloadData",
      JSON.stringify({
        task_id: editModalTask.id,
        title: editFormData.title.trim(),
        description: normalizeHtml(editFormData.description),
        existing_images: editFormData.existingImages,
      })
    );

    dispatch(updateTaskTracker(payload));
  };

  const confirmStatusUpdate = () => {
    if (!statusModalTask || !modalStatus) return;

    if (String(statusModalTask.status) === String(modalStatus.value)) {
      toast.info("Status is already set to this value");
      return;
    }

    setUpdatingStatusTaskId(statusModalTask.id);
    dispatch(
      updateTaskStatus({
        task_id: statusModalTask.id,
        status: parseInt(modalStatus.value, 10),
      })
    );
  };

  const handleAddComment = (taskId) => {
    const comment = normalizeHtml(commentDrafts[taskId]);
    if (!comment) {
      toast.error("Comment is required");
      return;
    }
    setSubmittingCommentTaskId(taskId);
    dispatch(addTaskComment({ task_id: taskId, comment }));
  };

  const formatCommentDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString();
  };

  return (
    <>
      <div className="row">
        <div className="col-12">
          <div className="page-title-box d-sm-flex align-items-center justify-content-between">
            <h4 className="mb-sm-0">Task Tracker</h4>
            <div className="page-title-right">
              <Breadcrumbs />
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-12">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title mb-0">Add New Task</h5>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit} encType="multipart/form-data">
                <div className="row g-3">
                  <div className="col-lg-6">
                    <label htmlFor="title" className="form-label">
                      Title <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      id="title"
                      name="title"
                      className="form-control"
                      placeholder="Enter title"
                      value={formData.title}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="col-lg-3">
                    <label htmlFor="priority" className="form-label">
                      Priority
                    </label>
                    <Select
                      inputId="priority"
                      className="basic-single"
                      classNamePrefix="select"
                      options={PRIORITY_OPTIONS}
                      value={formData.priority}
                      onChange={(selected) =>
                        setFormData((prev) => ({
                          ...prev,
                          priority: selected || PRIORITY_OPTIONS[1],
                        }))
                      }
                      placeholder="Select priority"
                      isSearchable={false}
                    />
                  </div>

                  <div className="col-lg-3">
                    <label htmlFor="images" className="form-label">
                      Images (multiple)
                    </label>
                    <input
                      type="file"
                      id="images"
                      name="images"
                      className="form-control"
                      accept="image/*"
                      multiple
                      onChange={handleImageChange}
                    />
                  </div>

                  <div className="col-lg-12">
                    <label htmlFor="description" className="form-label">
                      Description
                    </label>
                    <TextEditor
                      id="description"
                      placeholder="Enter description"
                      value={formData.description}
                      onChange={(value) =>
                        setFormData((prev) => ({ ...prev, description: value }))
                      }
                    />
                  </div>

                  {previewUrls.length > 0 && (
                    <div className="col-lg-12">
                      <div className="d-flex flex-wrap gap-2">
                        {previewUrls.map((url, index) => (
                          <div key={url} className="position-relative">
                            <img
                              src={url}
                              alt={`preview-${index}`}
                              style={{
                                width: 80,
                                height: 80,
                                objectFit: "cover",
                                borderRadius: 4,
                              }}
                            />
                            <button
                              type="button"
                              className="btn btn-sm btn-danger position-absolute top-0 end-0"
                              style={{ padding: "0 4px", fontSize: 10 }}
                              onClick={() => removeImage(index)}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="col-lg-12 d-flex gap-2">
                    <button
                      type="submit"
                      className="btn btn-sm btn-success"
                      disabled={requestStatus === "loading"}
                    >
                      {requestStatus === "loading" ? "Saving..." : "Submit"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      onClick={handleReset}
                    >
                      Reset
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-12">
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between">
              <h5 className="card-title mb-0">Task List</h5>
              <Link
                to="/task-tracker/done"
                className="btn btn-sm btn-success"
              >
                View Done Tasks
              </Link>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-bordered table-striped align-middle">
                  <thead>
                    <tr>
                      <th>Task ID</th>
                      <th>Title</th>
                      <th>Description</th>
                      <th>Images</th>
                      <th>Priority</th>
                      <th>Status</th>
                      <th>Created By</th>
                      <th>Created At</th>
                      <th>Actions</th>
                      <th>Comments</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.length === 0 ? (
                      <tr>
                        <td colSpan={10} className="text-center">
                          No tasks found
                        </td>
                      </tr>
                    ) : (
                      list.map((item) => (
                        <React.Fragment key={item.id}>
                          <tr>
                            <td>{item.id}</td>
                            <td>{item.title}</td>
                            <td style={{ maxWidth: 280 }}>
                              {item.description ? (
                                <div
                                  className="rich-text-content"
                                  dangerouslySetInnerHTML={{
                                    __html: item.description,
                                  }}
                                />
                              ) : (
                                "-"
                              )}
                            </td>
                            <td>
                              <div className="d-flex flex-wrap gap-1">
                                {(item.images || []).map((img) => (
                                  <a
                                    key={img}
                                    href={getImageUrl(img)}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    <img
                                      src={getImageUrl(img)}
                                      alt={img}
                                      style={{
                                        width: 48,
                                        height: 48,
                                        objectFit: "cover",
                                        borderRadius: 4,
                                      }}
                                    />
                                  </a>
                                ))}
                                {(!item.images || item.images.length === 0) &&
                                  "-"}
                              </div>
                            </td>
                            <td>
                              <span
                                className={`badge fs-5 px-2 py-1 me-2 ${getPriorityBadgeClass(item)}`}
                              >
                                {getPriorityLabel(item)}
                              </span>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => openPriorityModal(item)}
                              >
                                Change Priority
                              </button>
                            </td>
                            <td>
                              <span
                                className={`badge fs-5 px-2 py-1 me-2 ${getStatusBadgeClass(item)}`}
                              >
                                {getStatusLabel(item)}
                              </span>
                              <button
                                type="button"
                                className="btn btn-sm btn-warning"
                                onClick={() => openStatusModal(item)}
                              >
                                Change Status
                              </button>
                            </td>
                            <td>{item.created_by_name || "-"}</td>
                            <td>
                              {item.created_at
                                ? new Date(item.created_at).toLocaleString()
                                : "-"}
                            </td>
                            <td>
                              <div className="d-flex flex-wrap gap-1">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-primary"
                                  onClick={() => openEditModal(item)}
                                >
                                  Edit
                                </button>
                              </div>
                            </td>
                            <td>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => toggleComments(item.id)}
                              >
                                {expandedTaskId === item.id ? "Hide" : "View"} (
                                {item.comment_count ||
                                  item.comments?.length ||
                                  0}
                                )
                              </button>
                            </td>
                          </tr>
                          {expandedTaskId === item.id && (
                            <tr>
                              <td colSpan={10} className="bg-light">
                                <div className="p-3">
                                  <h6 className="mb-3">Comments</h6>
                                  {(item.comments || []).length === 0 ? (
                                    <p className="text-muted mb-3">
                                      No comments yet.
                                    </p>
                                  ) : (
                                    <ul className="list-unstyled mb-3">
                                      {(item.comments || []).map((c) => (
                                        <li
                                          key={c.id}
                                          className="border rounded p-2 mb-2 bg-white"
                                        >
                                          <div className="d-flex justify-content-between flex-wrap gap-1 mb-1">
                                            <strong>
                                              {c.user_name || "Unknown user"}
                                            </strong>
                                            <small className="text-muted">
                                              {formatCommentDate(c.created_at)}
                                            </small>
                                          </div>
                                          <div
                                            className="mb-0 rich-text-content"
                                            dangerouslySetInnerHTML={{
                                              __html: c.comment,
                                            }}
                                          />
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                  <div className="row g-2 align-items-end">
                                    <div className="col-lg-10">
                                      <label
                                        htmlFor={`comment-${item.id}`}
                                        className="form-label"
                                      >
                                        Add comment
                                      </label>
                                      <TextEditor
                                        id={`comment-${item.id}`}
                                        className="text-editor-sm"
                                        placeholder="Write your comment..."
                                        value={commentDrafts[item.id] || ""}
                                        onChange={(value) =>
                                          handleCommentDraftChange(item.id, value)
                                        }
                                      />
                                    </div>
                                    <div className="col-lg-2">
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-success w-100"
                                        disabled={
                                          commentStatus === "loading" &&
                                          submittingCommentTaskId === item.id
                                        }
                                        onClick={() => handleAddComment(item.id)}
                                      >
                                        {commentStatus === "loading" &&
                                        submittingCommentTaskId === item.id
                                          ? "Adding..."
                                          : "Add"}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        show={showEditModal}
        onHide={closeEditModal}
        backdrop="static"
        keyboard={false}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Task</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row g-3">
            <div className="col-12">
              <label htmlFor="edit-title" className="form-label">
                Title <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                id="edit-title"
                name="title"
                className="form-control"
                placeholder="Enter title"
                value={editFormData.title}
                onChange={handleEditFormChange}
              />
            </div>
            <div className="col-12">
              <label htmlFor="edit-description" className="form-label">
                Description
              </label>
              <TextEditor
                id="edit-description"
                placeholder="Enter description"
                value={editFormData.description}
                onChange={(value) =>
                  setEditFormData((prev) => ({
                    ...prev,
                    description: value,
                  }))
                }
              />
            </div>
            {editFormData.existingImages.length > 0 && (
              <div className="col-12">
                <label className="form-label">Existing Images</label>
                <div className="d-flex flex-wrap gap-2">
                  {editFormData.existingImages.map((img, index) => (
                    <div key={img} className="position-relative">
                      <img
                        src={getImageUrl(img)}
                        alt={img}
                        style={{
                          width: 80,
                          height: 80,
                          objectFit: "cover",
                          borderRadius: 4,
                        }}
                      />
                      <button
                        type="button"
                        className="btn btn-sm btn-danger position-absolute top-0 end-0"
                        style={{ padding: "0 4px", fontSize: 10 }}
                        onClick={() => removeExistingEditImage(index)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="col-12">
              <label htmlFor="edit-images" className="form-label">
                Add Images
              </label>
              <input
                type="file"
                id="edit-images"
                className="form-control"
                accept="image/*"
                multiple
                onChange={handleEditImageChange}
              />
            </div>
            {editPreviewUrls.length > 0 && (
              <div className="col-12">
                <label className="form-label">New Image Previews</label>
                <div className="d-flex flex-wrap gap-2">
                  {editPreviewUrls.map((url, index) => (
                    <div key={url} className="position-relative">
                      <img
                        src={url}
                        alt={`edit-preview-${index}`}
                        style={{
                          width: 80,
                          height: 80,
                          objectFit: "cover",
                          borderRadius: 4,
                        }}
                      />
                      <button
                        type="button"
                        className="btn btn-sm btn-danger position-absolute top-0 end-0"
                        style={{ padding: "0 4px", fontSize: 10 }}
                        onClick={() => removeNewEditImage(index)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" onClick={closeEditModal}>
            Cancel
          </Button>
          <Button
            variant="success"
            size="sm"
            onClick={confirmEditTask}
            disabled={editStatus === "loading"}
          >
            {editStatus === "loading" ? "Saving..." : "Save Changes"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showPriorityModal}
        onHide={closePriorityModal}
        backdrop="static"
        keyboard={false}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Change Priority</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {priorityModalTask && (
            <>
              <p className="mb-2">
                <strong>Task:</strong> {priorityModalTask.title}
              </p>
              <p className="mb-3">
                Current priority:{" "}
                <span
                  className={`badge ${getPriorityBadgeClass(priorityModalTask)}`}
                >
                  {getPriorityLabel(priorityModalTask)}
                </span>
              </p>
              <label htmlFor="modal-priority" className="form-label">
                New Priority <span className="text-danger">*</span>
              </label>
              <Select
                inputId="modal-priority"
                className="basic-single"
                classNamePrefix="select"
                options={PRIORITY_OPTIONS}
                value={modalPriority}
                onChange={(selected) =>
                  setModalPriority(selected || PRIORITY_OPTIONS[1])
                }
                placeholder="Select priority"
                isSearchable={false}
              />
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" onClick={closePriorityModal}>
            Cancel
          </Button>
          <Button
            variant="success"
            size="sm"
            onClick={confirmPriorityUpdate}
            disabled={
              priorityUpdateStatus === "loading" &&
              updatingPriorityTaskId === priorityModalTask?.id
            }
          >
            {priorityUpdateStatus === "loading" &&
            updatingPriorityTaskId === priorityModalTask?.id
              ? "Updating..."
              : "Update Priority"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showStatusModal}
        onHide={closeStatusModal}
        backdrop="static"
        keyboard={false}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Change Status</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {statusModalTask && (
            <>
              <p className="mb-2">
                <strong>Task:</strong> {statusModalTask.title}
              </p>
              <p className="mb-3">
                Current status:{" "}
                <span
                  className={`badge ${getStatusBadgeClass(statusModalTask)}`}
                >
                  {getStatusLabel(statusModalTask)}
                </span>
              </p>
              <label htmlFor="modal-status" className="form-label">
                New Status <span className="text-danger">*</span>
              </label>
              <Select
                inputId="modal-status"
                className="basic-single"
                classNamePrefix="select"
                options={STATUS_OPTIONS}
                value={modalStatus}
                onChange={(selected) =>
                  setModalStatus(selected || STATUS_OPTIONS[0])
                }
                placeholder="Select status"
                isSearchable={false}
              />
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" onClick={closeStatusModal}>
            Cancel
          </Button>
          <Button
            variant="success"
            size="sm"
            onClick={confirmStatusUpdate}
            disabled={
              statusUpdateStatus === "loading" &&
              updatingStatusTaskId === statusModalTask?.id
            }
          >
            {statusUpdateStatus === "loading" &&
            updatingStatusTaskId === statusModalTask?.id
              ? "Updating..."
              : "Update Status"}
          </Button>
        </Modal.Footer>
      </Modal>

    </>
  );
};

export default TaskTracker;
